/**
 * Feature-flag gating for the left rail (and the landing page).
 *
 * Some modules are dark-launched behind a client-visible flag from
 * GET /api/whoami `features` (read through useFeatureFlag, which is FAIL-
 * CLOSED: loading, error, older server, missing key → off). A rail item listed
 * here is shown only while its flag is on. This runs AFTER the role gate
 * (gatePlatformNav), so a flag-gated module needs BOTH: the role gate's answer
 * and the flag.
 *
 * Kept apart from nav-gating.ts on purpose: that file answers "can this role
 * reach the module"; this one answers "is the module switched on". Same
 * root-segment keying as nav-gating's EXPLICIT_GRANT_ONLY_NAV.
 *
 * UI affordance only. The surface behind a flag enforces server-side too (the
 * commission API checks review:commissions AND the flag on every request).
 */
import type { NavItem } from '@andavo/web-ui';
import { useMemo } from 'react';
import { useFeatureFlag } from '@/auth/useWhoami';
import { COMMISSIONS_WORKBENCH_FLAG } from '@/features/commissions';
import { gatePlatformNav } from './nav-gating';

/** Rail root segment → the client-visible flag that must be on. */
export const FLAG_GATED_NAV: ReadonlyMap<string, string> = new Map([
  ['commissions', COMMISSIONS_WORKBENCH_FLAG],
]);

function rootSegment(path: NavItem['path']): string {
  const p = typeof path === 'string' ? path : (path.pathname ?? '');
  return p.split('/').filter(Boolean)[0] ?? '';
}

/** The flag a path's module is gated on, if any. */
export function flagForPath(
  path: NavItem['path'],
  gated: ReadonlyMap<string, string> = FLAG_GATED_NAV,
): string | undefined {
  return gated.get(rootSegment(path));
}

/** Drop every flag-gated item whose flag is not in `enabled`. */
export function gateNavByFlags<T extends Pick<NavItem, 'path'>>(
  items: readonly T[],
  enabled: ReadonlySet<string>,
  gated: ReadonlyMap<string, string> = FLAG_GATED_NAV,
): T[] {
  return items.filter((item) => {
    const flag = flagForPath(item.path, gated);
    return flag === undefined || enabled.has(flag);
  });
}

/** The platform rail for this user: role gate first, then flags. */
export function composeRail(
  items: NavItem[],
  storedRoles: readonly string[] | undefined,
  isAdmin: boolean,
  enabledFlags: ReadonlySet<string>,
): NavItem[] {
  return gateNavByFlags(
    gatePlatformNav(items, storedRoles, isAdmin),
    enabledFlags,
  );
}

/**
 * The rail-relevant flags that are ON for the signed-in user. One
 * useFeatureFlag call per flag in FLAG_GATED_NAV (add a line when you add one).
 */
export function useEnabledNavFlags(): ReadonlySet<string> {
  const commissions = useFeatureFlag(COMMISSIONS_WORKBENCH_FLAG);
  return useMemo(
    () => new Set(commissions ? [COMMISSIONS_WORKBENCH_FLAG] : []),
    [commissions],
  );
}
